import type { JobExecutor } from './types';
import type { ExecutorFactory } from './types';

/**
 * Registry of executor factories keyed by capability id. The whitelabel's
 * `BrandConfig.executorAllowlist` is enforced at registration time: a
 * factory whose name is not in the allowlist throws on `register()`.
 *
 * The Sifter build, for example, allows only `claude-code`, `http-api`,
 * `webhook`, and `custom-script`; attempting to register `anthropic-mcp`
 * throws at startup. Operators get a verifiable claim that a misconfigured
 * config cannot enable a tool-using executor.
 *
 * Pass `['*']` (the unbranded shuttle dev distribution) to disable
 * allowlist enforcement.
 */
export class ExecutorRegistry {
  private readonly factories = new Map<string, ExecutorFactory>();
  private readonly allowlist: ReadonlyArray<string>;

  constructor(allowlist: ReadonlyArray<string> = ['*']) {
    this.allowlist = allowlist;
  }

  register(name: string, factory: ExecutorFactory): void {
    if (!this.isAllowed(name)) {
      throw new Error(
        `Executor "${name}" is not enabled for this build. ` +
          `Allowed executors: ${this.allowlist.join(', ')}`,
      );
    }
    this.factories.set(name, factory);
  }

  create(name: string, config: Record<string, unknown>): JobExecutor {
    const factory = this.factories.get(name);
    if (!factory) {
      throw new Error(`Executor not registered: ${name}`);
    }
    return factory(config, name);
  }

  has(name: string): boolean {
    return this.factories.has(name);
  }

  get allowedExecutors(): ReadonlyArray<string> {
    return this.allowlist;
  }

  /** All registered factories as JobExecutors built with their default config. */
  build(configs: Record<string, Record<string, unknown>>): JobExecutor[] {
    const out: JobExecutor[] = [];
    for (const [name, cfg] of Object.entries(configs)) {
      if (!this.factories.has(name)) {
        throw new Error(
          `Executor "${name}" in config is not registered or not enabled for this build.`,
        );
      }
      out.push(this.create(name, cfg));
    }
    return out;
  }

  private isAllowed(name: string): boolean {
    return this.allowlist[0] === '*' || this.allowlist.includes(name);
  }
}
